'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import cx from 'classnames';

import { Button } from '@/components/Default/Button';
import { Input } from '@/components/Default/Input';

const formatPostalCode = (value) => {
    return value.replace(/\s/g, '').toUpperCase();
};

const isPostalCodeValid = (value) => {
    const re = new RegExp('^[0-9]{4}[A-Z]{2}$', 'g');
    const subject = formatPostalCode(value);

    return re.test(subject);
};

const formatHouseNumber = (value) => {
    return value.replace(/\s/g, '');
};

const isHouseNumberValid = (value) => {
    return formatHouseNumber(value).length > 0;
};

const isHouseNumberAffixValid = () => {
    return true;
};

const Default = () => {
    const [postalCode, setPostalCode] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [houseNumberAffix, setHouseNumberAffix] = useState('');

    const [shouldValidate, setValidate] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(
        () => {
            if (shouldValidate && errors.length === 0) {
                const data = {
                    postal_code: formatPostalCode(postalCode),
                    house_number: formatHouseNumber(houseNumber),
                    house_number_affix: houseNumberAffix
                };

                const params = new URLSearchParams();
                params.set('postcode', data.postal_code);
                params.set('huisnr', data.house_number);
                params.set('toev', data.house_number_affix || '');

                location.href = 'https://configurator.timeforsolar.nl/zonnepanelen/?' + params.toString();
            }
        },
        [houseNumber, houseNumberAffix, postalCode, shouldValidate, errors]
    );

    const handleOnSubmitRequest = () => {
        // Validate entries first.
        setErrors(() => {
            const s = [...[]];

            if (! isPostalCodeValid(postalCode)) {
                s.push('postal_code');
            }

            if (! isHouseNumberValid(houseNumber)) {
                s.push('house_nr');
            }

            if (! isHouseNumberAffixValid(houseNumberAffix)) {
                s.push('house_nr_affix');
            }

            return s;
        });

        setValidate(true);
    };

    const reasons = [
        { label: 'Profiteer van 0% BTW' },
        { label: 'Flexibele aanpassingen mogelijk' },
        { label: 'Expertadvies op maat' }
    ];

    return (
        <motion.div className='absolute z-30 w-full sm:max-w-xl h-full sm:h-auto bg-black/20 sm:bg-transparent text-black px-0 pt-20 sm:py-16 sm:px-12'
            initial={{
                y: 150,
                opacity: 0
            }}
            animate={{
                y: 0,
                opacity: 1,
                transition: {
                    delay: 0.5
                }
            }}
        >
            <motion.div className='text-black sm:bg-white rounded-none sm:rounded-lg py-8 px-6 sm:py-16 sm:px-12 shadow-none sm:shadow-center'>
                <h1 className='text-2xl sm:text-4xl text-white sm:text-black font-semibold sm:font-bold tracking-tight'>
                    Bereken binnen 1 minuut jouw voordeel
                </h1>

                <hr className='w-full h-1 sm:h-2 bg-gradient-to-r rounded-sm from-primary-500 to-transparent my-6 border-none' />

                <p className='text-gray-100 sm:text-black text-base sm:text-xl mb-4'>
                    Vul je gegevens in en bereken direct je prijs en besparing.
                </p>

                <ol className='text-gray-100 sm:text-black px-2 space-y-2 my-6'>
                    {reasons.map((reason, idx) => (
                        <li key={idx} className='flex items-start gap-4 sm:text-xl font-semibold'>
                            <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor' className='shrink-0 w-8 h-8 stroke-primary-500'>
                                <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5' />
                            </svg>
                            {reason.label}
                        </li>
                    ))}
                </ol>

                <section className='grid grid-cols-2 sm:grid-cols-4 gap-2'>
                    <div className='col-span-2'>
                        <label htmlFor='postal_code' className='block font-semibold text-gray-100 sm:text-black mb-1'>
                            Postcode
                        </label>
                        <Input
                            id='postal_code'
                            error={errors.includes('postal_code')}
                            onChange={value => {
                                setValidate(false);
                                setPostalCode(value);
                                setErrors(value => value.filter(val => val !== 'postal_code'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                    <div className='col-span-1'>
                        <label htmlFor='house_nr' className='block font-semibold text-gray-100 sm:text-black mb-1'>
                            Huisnr.
                        </label>
                        <Input
                            id='house_nr'
                            error={errors.includes('house_nr')}
                            onChange={value => {
                                setValidate(false);
                                setHouseNumber(value);
                                setErrors(value => value.filter(val => val !== 'house_nr'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                    <div>
                        <label htmlFor='house_nr_affix' className='block font-semibold text-gray-100 sm:text-black mb-1'>
                            Toev.
                        </label>
                        <Input
                            id='house_nr_affix'
                            error={errors.includes('house_nr_affix')}
                            onChange={value => {
                                setValidate(false);
                                setHouseNumberAffix(value);
                                setErrors(value => value.filter(val => val !== 'house_nr_affix'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                </section>

                <Button label='Check nu!' className='mt-4 hover:bg-orange-700 hover:border-orange-700 sm:ml-auto' animated={true} size='medium' onClick={handleOnSubmitRequest} />
            </motion.div>
        </motion.div>
    );
};

const Minimal = ({ className = '' }) => {
    const [postalCode, setPostalCode] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [houseNumberAffix, setHouseNumberAffix] = useState('');

    const [shouldValidate, setValidate] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(
        () => {
            if (shouldValidate && errors.length === 0) {
                const data = {
                    postal_code: formatPostalCode(postalCode),
                    house_number: formatHouseNumber(houseNumber),
                    house_number_affix: houseNumberAffix
                };

                const params = new URLSearchParams();
                params.set('postcode', data.postal_code);
                params.set('huisnr', data.house_number);
                params.set('toev', data.house_number_affix || '');

                location.href = 'https://configurator.timeforsolar.nl/zonnepanelen/?' + params.toString();
                /*
                StatisticService.storeRequest(data).then(() => {
                    const params = new URLSearchParams();
                    params.set('postcode', data.postal_code);
                    params.set('huisnr', data.house_number);
                    params.set('toev', data.house_number_affix || DataTypes.EmptyString);

                    location.href = '/?' + params.toString();
                });
                */
            }
        },
        [houseNumber, houseNumberAffix, postalCode, shouldValidate, errors]
    );

    const handleOnSubmitRequest = () => {
        // Validate entries first.
        setErrors(() => {
            const s = [...[]];

            if (! isPostalCodeValid(postalCode)) {
                s.push('postal_code');
            }

            if (! isHouseNumberValid(houseNumber)) {
                s.push('house_nr');
            }

            if (! isHouseNumberAffixValid(houseNumberAffix)) {
                s.push('house_nr_affix');
            }

            return s;
        });

        setValidate(true);
    };

    return (
        <div className={cx('rounded-lg border flex flex-col sm:flex-row gap-8 py-8 px-8 shadow-sm', {
            'bg-white': ! className.includes('bg-'),
            [className]: Boolean(className)
        })}>
            <div className='flex flex-col items-start'>
                <section className='grid grid-cols-2 sm:grid-cols-4 gap-2'>
                    <div className='col-span-2'>
                        <label htmlFor='postal_code' className='block font-semibold mb-1'>
                            Postcode
                        </label>
                        <Input
                            id='postal_code'
                            error={errors.includes('postal_code')}
                            onChange={value => {
                                setValidate(false);
                                setPostalCode(value);
                                setErrors(value => value.filter(val => val !== 'postal_code'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                    <div className='col-span-1'>
                        <label htmlFor='house_nr' className='block font-semibold mb-1'>
                            Huisnr.
                        </label>
                        <Input
                            id='house_nr'
                            error={errors.includes('house_nr')}
                            onChange={value => {
                                setValidate(false);
                                setHouseNumber(value);
                                setErrors(value => value.filter(val => val !== 'house_nr'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                    <div>
                        <label htmlFor='house_nr_affix' className='block font-semibold mb-1'>
                            Toev.
                        </label>
                        <Input
                            id='house_nr_affix'
                            error={errors.includes('house_nr_affix')}
                            onChange={value => {
                                setValidate(false);
                                setHouseNumberAffix(value);
                                setErrors(value => value.filter(val => val !== 'house_nr_affix'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                </section>
                <Button label='Check mijn dak!' className='mt-4 hover:bg-orange-700 hover:border-orange-700' animated={true} size='small' onClick={handleOnSubmitRequest} />
            </div>
            <div className='px-0 sm:px-12'>
                <h1 className='text-xl sm:text-3xl font-semibold tracking-wide my-2'>
                    Doe vandaag nog de dakcheck
                </h1>
                <p className='text-lg'>
                    Bereken hoeveel zonnepanelen jou kunnen opleveren.
                </p>
            </div>
        </div>
    );
};

const Plain = ({ className = '' }) => {
    const [postalCode, setPostalCode] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [houseNumberAffix, setHouseNumberAffix] = useState('');

    const [shouldValidate, setValidate] = useState(false);
    const [errors, setErrors] = useState([]);

    useEffect(
        () => {
            if (shouldValidate && errors.length === 0) {
                const data = {
                    postal_code: formatPostalCode(postalCode),
                    house_number: formatHouseNumber(houseNumber),
                    house_number_affix: houseNumberAffix
                };

                const params = new URLSearchParams();
                params.set('postcode', data.postal_code);
                params.set('huisnr', data.house_number);
                params.set('toev', data.house_number_affix || '');

                location.href = 'https://configurator.timeforsolar.nl/zonnepanelen/?' + params.toString();
                /*
                StatisticService.storeRequest(data).then(() => {
                    const params = new URLSearchParams();
                    params.set('postcode', data.postal_code);
                    params.set('huisnr', data.house_number);
                    params.set('toev', data.house_number_affix || DataTypes.EmptyString);

                    location.href = '/?' + params.toString();
                });
                */
            }
        },
        [houseNumber, houseNumberAffix, postalCode, shouldValidate, errors]
    );

    const handleOnSubmitRequest = () => {
        // Validate entries first.
        setErrors(() => {
            const s = [...[]];

            if (! isPostalCodeValid(postalCode)) {
                s.push('postal_code');
            }

            if (! isHouseNumberValid(houseNumber)) {
                s.push('house_nr');
            }

            if (! isHouseNumberAffixValid(houseNumberAffix)) {
                s.push('house_nr_affix');
            }

            return s;
        });

        setValidate(true);
    };

    return (
        <div className={cx('flex gap-8 py-8 px-0 sm:px-8 shadow-sm', {
            [className]: Boolean(className)
        })}>
            <div className='flex flex-col items-start'>
                <div>
                    <h1 className='text-white text-3xl font-semibold tracking-wide my-2'>
                        Doe vandaag nog de dakcheck
                    </h1>
                    <p className='text-lg'>
                        Bereken hoeveel zonnepanelen jou kunnen opleveren.
                    </p>
                </div>
                <section className='grid grid-cols-4 gap-2 mt-4'>
                    <div className='col-span-2 text-black'>
                        <label htmlFor='footer_postal_code' className='block font-semibold mb-1 text-gray-300'>
                            Postcode
                        </label>
                        <Input
                            id='footer_postal_code'
                            error={errors.includes('postal_code')}
                            onChange={value => {
                                setValidate(false);
                                setPostalCode(value);
                                setErrors(value => value.filter(val => val !== 'postal_code'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                    <div className='col-span-1 text-black'>
                        <label htmlFor='footer_house_nr' className='block font-semibold mb-1 text-gray-300'>
                            Huisnr.
                        </label>
                        <Input
                            id='footer_house_nr'
                            error={errors.includes('house_nr')}
                            onChange={value => {
                                setValidate(false);
                                setHouseNumber(value);
                                setErrors(value => value.filter(val => val !== 'house_nr'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                    <div className='col-span-1 text-black'>
                        <label htmlFor='footer_house_nr_affix' className='block font-semibold mb-1 text-gray-300'>
                            Toev.
                        </label>
                        <Input
                            id='footer_house_nr_affix'
                            error={errors.includes('house_nr_affix')}
                            onChange={value => {
                                setValidate(false);
                                setHouseNumberAffix(value);
                                setErrors(value => value.filter(val => val !== 'house_nr_affix'));
                            }}
                            onEnter={handleOnSubmitRequest}
                        />
                    </div>
                </section>
                <Button label='Check mijn dak!' className='mt-4 border-white hover:bg-white hover:border-white hover:text-black' animated={true} size='small' onClick={handleOnSubmitRequest} />
            </div>
        </div>
    );
};

export { Default, Minimal, Plain };
